import { useCallback, useEffect, useSyncExternalStore } from 'react';

import { useFeatureFlag } from '@betterment/shared-components';
import { useAtom } from 'jotai';
import { RESET, atomWithStorage } from 'jotai/utils';

export const COLOR_MODE_KEY = 'bmt-color-mode';

export type ColorMode = 'light' | 'dark';

export type UseColorModeResult = {
  colorMode: ColorMode;
  setColorMode: (colorMode: ColorMode) => void;
  matchSystem: () => void;
};

const preferredColorModeAtom = atomWithStorage<ColorMode | undefined>(COLOR_MODE_KEY, undefined, undefined, {
  getOnInit: true,
});

const systemColorSchemeMediaQuery = window.matchMedia('(prefers-color-scheme: dark)');

function subscribeToSystemColorScheme(onChange: () => void) {
  systemColorSchemeMediaQuery.addEventListener('change', onChange);
  return () => systemColorSchemeMediaQuery.removeEventListener('change', onChange);
}

function getSystemColorMode(): ColorMode {
  return systemColorSchemeMediaQuery.matches ? 'dark' : 'light';
}

export function useColorMode(): UseColorModeResult {
  const { enabled: isEnabled } = useFeatureFlag({
    splitName: 'retail.color_mode_q1_2025_enabled',
    context: 'color-mode-state',
  });

  const [preferredColorMode, setPreferredColorMode] = useAtom(preferredColorModeAtom);
  const matchSystem = useCallback(() => setPreferredColorMode(RESET), [setPreferredColorMode]);

  const systemColorMode = useSyncExternalStore(subscribeToSystemColorScheme, getSystemColorMode);
  const resolvedColorMode = isEnabled ? (preferredColorMode ?? systemColorMode) : 'light';

  useEffect(() => {
    if (isEnabled) {
      document.documentElement.setAttribute('data-color-mode', resolvedColorMode);
    }
  }, [isEnabled, resolvedColorMode]);

  return { colorMode: resolvedColorMode, setColorMode: setPreferredColorMode, matchSystem };
}
